import React, { useMemo, useRef, useState } from "react";

import { twJoin } from "tailwind-merge";

import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import {
  DateTimePicker,
  DateTimePickerProps,
} from "src/base-components/DateTimePicker";
import { CustomPopover } from "src/base-components/Popover";
import { useOnClickOutside } from "src/hooks/usehooks-ts";

const parseDate = (v: unknown): Date | undefined => {
  if (!v || typeof v !== "string") return undefined;
  const parsed = new Date(v);
  return isNaN(parsed.getTime()) ? undefined : parsed;
};

export const DateEditor: React.FC<{
  value: unknown;
  dateOnly: boolean;
  onChange: (newValue: unknown) => void;
  onBlur: () => void;
  hasLeftIcon?: boolean;
}> = ({ value, dateOnly, onChange, onBlur, hasLeftIcon }) => {
  const [originalValue] = useState(value);
  const originalDate = useMemo(() => parseDate(originalValue), [originalValue]);
  const dateValue = useMemo(() => parseDate(value), [value]);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useOnClickOutside([triggerRef, panelRef], (e) => {
    if ((e.target as Element)?.closest?.("[data-headlessui-portal]")) return;
    onBlur();
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onBlur();
    }
  };

  const handleChange: DateTimePickerProps["onChange"] = (date) => {
    if (!date) {
      onChange(null);
      return;
    }
    if (originalDate && date.getTime() === originalDate.getTime()) {
      onChange(originalValue);
      return;
    }
    onChange(date.toISOString());
  };

  return (
    <div ref={triggerRef} className="h-full w-full" onKeyDown={handleKeyDown}>
      <CustomPopover
        button={
          <div
            className={twJoin(
              "flex h-full w-full items-center overflow-hidden whitespace-nowrap py-1.5 pr-2",
              hasLeftIcon ? "pl-6" : "pl-1.5",
            )}
          >
            {dateValue ? (
              dateOnly ? (
                <DisplayDate value={dateValue.toISOString()} />
              ) : (
                <DisplayDateTime value={dateValue.toISOString()} />
              )
            ) : (
              "Pick a date"
            )}
          </div>
        }
        isOpen={true}
        offsetX={8}
        placement="bottom-start"
        wrapperClassName="w-full"
      >
        <div ref={panelRef}>
          <DateTimePicker
            dateOnly={dateOnly}
            value={dateValue}
            allowFutureDates
            onChange={handleChange}
          />
        </div>
      </CustomPopover>
    </div>
  );
};
